import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Trash2,
  Shield,
  User,
  CheckCircle,
  AlertCircle,
  Edit,
  RotateCcw,
  Users,
  UserPlus,
  CheckSquare,
  Square,
  Search,
  Key,
  RefreshCw,
} from "lucide-react";

export default function KelolaAkun() {
  const [activeTab, setActiveTab] = useState("list"); // 'list' | 'single' | 'bulk'

  const [akunList, setAkunList] = useState([]);
  const [siswaList, setSiswaList] = useState([]);
  const [tutorList, setTutorList] = useState([]);
  const [loading, setLoading] = useState(true);

  // STATE NOTIFIKASI
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // STATE SINGLE EDIT/ADD
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "siswa",
    nama_lengkap: "",
  });

  // STATE BULK ADD
  const [bulkRole, setBulkRole] = useState("siswa");
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [bulkSearch, setBulkSearch] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  async function fetchData() {
    setLoading(true);
    const { data: dataAkun } = await supabase
      .from("akun")
      .select("*")
      .order("id", { ascending: false });
    const { data: dataSiswa } = await supabase
      .from("siswa")
      .select("*")
      .order("nama_lengkap");
    const { data: dataTutor } = await supabase
      .from("tutor")
      .select("*")
      .order("nama_lengkap");

    setAkunList(dataAkun || []);
    setSiswaList(dataSiswa || []);
    setTutorList(dataTutor || []);
    setLoading(false);
  }

  // --- LOGIC SINGLE ADD/EDIT ---
  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.username || !formData.password || !formData.nama_lengkap)
      return showToast("Semua data wajib diisi!", "error");

    try {
      if (isEditing) {
        const { data: cekUser } = await supabase
          .from("akun")
          .select("id")
          .eq("username", formData.username)
          .neq("id", editId)
          .single();
        if (cekUser) return showToast("Username sudah dipakai!", "error");
        await supabase.from("akun").update(formData).eq("id", editId);
        showToast("Data akun diperbarui!");
      } else {
        const { data: cekUser } = await supabase
          .from("akun")
          .select("id")
          .eq("username", formData.username)
          .single();
        if (cekUser) return showToast("Username sudah terdaftar!", "error");
        await supabase.from("akun").insert([formData]);
        showToast("Akun baru berhasil dibuat!");
      }
      resetForm();
      setActiveTab("list");
      fetchData();
    } catch (err) {
      showToast("Gagal: " + err.message, "error");
    }
  }

  async function handleDelete(id) {
    if (confirm("Hapus akun ini permanen?")) {
      await supabase.from("akun").delete().eq("id", id);
      showToast("Akun dihapus.");
      fetchData();
    }
  }

  function handleEditClick(item) {
    setIsEditing(true);
    setEditId(item.id);
    setFormData({
      username: item.username,
      password: item.password,
      role: item.role,
      nama_lengkap: item.nama_lengkap,
    });
    setActiveTab("single");
  }

  function resetForm() {
    setIsEditing(false);
    setEditId(null);
    setFormData({
      username: "",
      password: "",
      role: "siswa",
      nama_lengkap: "",
    });
  }

  // --- LOGIC BULK ADD (MASSAL) ---

  const getCandidates = () => {
    const sourceList = bulkRole === "siswa" ? siswaList : tutorList;
    let candidates = sourceList.filter((person) => {
      const hasAccount = akunList.some(
        (akun) =>
          akun.nama_lengkap.toLowerCase() === person.nama_lengkap.toLowerCase()
      );
      return !hasAccount;
    });
    if (bulkSearch) {
      candidates = candidates.filter((c) =>
        c.nama_lengkap.toLowerCase().includes(bulkSearch.toLowerCase())
      );
    }
    return candidates;
  };

  const candidatesData = getCandidates();

  const toggleCandidate = (name) => {
    if (selectedCandidates.includes(name))
      setSelectedCandidates((prev) => prev.filter((n) => n !== name));
    else setSelectedCandidates((prev) => [...prev, name]);
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === candidatesData.length)
      setSelectedCandidates([]);
    else setSelectedCandidates(candidatesData.map((c) => c.nama_lengkap));
  };

  // FUNGSI AUTO GENERATE
  const generateCredentials = (fullName) => {
    // Ambil nama depan saja, huruf kecil, buang simbol
    const firstName = fullName
      .split(" ")[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

    // Username: nama + 3 angka acak (100-999)
    const username = `${firstName}${Math.floor(100 + Math.random() * 900)}`;

    // Password: nama + 4 angka acak (1000-9999)
    const password = `${firstName}${Math.floor(1000 + Math.random() * 9000)}`;

    return { username, password };
  };

  const handleBulkSubmit = async () => {
    if (selectedCandidates.length === 0)
      return showToast("Pilih minimal 1 orang!", "error");

    setIsProcessing(true);
    try {
      // Generate Data Unik Per Orang
      const payload = selectedCandidates.map((nama) => {
        const creds = generateCredentials(nama);
        return {
          nama_lengkap: nama,
          role: bulkRole,
          username: creds.username,
          password: creds.password,
        };
      });

      // Insert Massal
      const { error } = await supabase.from("akun").insert(payload);
      if (error) throw error;

      showToast(`Sukses membuat ${payload.length} akun baru!`);
      setSelectedCandidates([]);
      fetchData();
      setActiveTab("list");
    } catch (error) {
      showToast("Gagal: " + error.message, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  // --- STYLES ---
  const styles = {
    container: {
      padding: "30px",
      maxWidth: "1000px",
      margin: "0 auto",
      fontFamily: "'Inter', sans-serif",
      background: "#F8FAFC",
      minHeight: "100vh",
    },
    tabContainer: {
      display: "flex",
      gap: "10px",
      marginBottom: "25px",
      borderBottom: "1px solid #E2E8F0",
      paddingBottom: "10px",
    },
    tabBtn: (isActive) => ({
      padding: "10px 20px",
      borderRadius: "8px",
      border: "none",
      cursor: "pointer",
      fontWeight: "600",
      fontSize: "14px",
      background: isActive ? "#2563EB" : "transparent",
      color: isActive ? "white" : "#64748B",
      transition: "0.2s",
    }),
    card: {
      background: "white",
      padding: "25px",
      borderRadius: "16px",
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
    },
    inputGroup: { marginBottom: "15px" },
    label: {
      display: "block",
      marginBottom: "6px",
      fontWeight: "600",
      fontSize: "13px",
      color: "#64748B",
      textTransform: "uppercase",
    },
    input: {
      width: "100%",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid #CBD5E1",
      boxSizing: "border-box",
      fontSize: "14px",
      outline: "none",
    },
    select: {
      width: "100%",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid #CBD5E1",
      boxSizing: "border-box",
      background: "white",
      fontSize: "14px",
    },
    btnSave: {
      background: "#2563EB",
      color: "white",
      padding: "12px 20px",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontWeight: "600",
    },
    btnCancel: {
      background: "#F1F5F9",
      color: "#64748B",
      padding: "12px 20px",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontWeight: "600",
    },
    toastContainer: {
      position: "fixed",
      top: "20px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 20px",
      borderRadius: "12px",
      background: "white",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      border: "1px solid #E2E8F0",
      minWidth: "300px",
      animation: "slideDown 0.3s ease-out",
    },
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "#FCE7F3";
      case "kepala":
        return "#E0F2FE";
      case "operator":
        return "#DCFCE7";
      case "tutor":
        return "#FFEDD5";
      default:
        return "#F1F5F9";
    }
  };

  return (
    <div style={styles.container}>
      {/* TOAST */}
      {toast.show && (
        <div
          style={{
            ...styles.toastContainer,
            borderLeft:
              toast.type === "success"
                ? "4px solid #10B981"
                : "4px solid #EF4444",
          }}
        >
          {toast.type === "success" ? (
            <CheckCircle size={20} color="#059669" />
          ) : (
            <AlertCircle size={20} color="#DC2626" />
          )}
          <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
            {toast.message}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 30 }}>
        <Link
          to="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
            textDecoration: "none",
            color: "#64748B",
            marginBottom: "15px",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <ArrowLeft size={18} /> Kembali ke Dashboard
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 15 }}>
          <div style={{ background: "#DBEAFE", padding: 12, borderRadius: 16 }}>
            <Shield size={32} color="#2563EB" />
          </div>
          <div>
            <h2 style={{ margin: 0, color: "#1E293B", fontSize: 24 }}>
              Kelola Pengguna
            </h2>
            <p style={{ margin: 0, color: "#64748B", fontSize: 14 }}>
              Manajemen akun login aplikasi
            </p>
          </div>
        </div>
      </div>

      <div style={styles.tabContainer}>
        <button
          style={styles.tabBtn(activeTab === "list")}
          onClick={() => setActiveTab("list")}
        >
          <Users size={16} style={{ marginRight: 5 }} /> Daftar Akun
        </button>
        <button
          style={styles.tabBtn(activeTab === "single")}
          onClick={() => {
            resetForm();
            setActiveTab("single");
          }}
        >
          <UserPlus size={16} style={{ marginRight: 5 }} /> Tambah Manual
        </button>
        <button
          style={styles.tabBtn(activeTab === "bulk")}
          onClick={() => setActiveTab("bulk")}
        >
          <RefreshCw size={16} style={{ marginRight: 5 }} /> Generate Otomatis
        </button>
      </div>

      {/* --- TAB 1: LIST --- */}
      {activeTab === "list" && (
        <div style={styles.card}>
          <h3 style={{ marginTop: 0, color: "#334155" }}>
            Pengguna Terdaftar ({akunList.length})
          </h3>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div style={{ display: "grid", gap: "12px" }}>
              {akunList.map((item) => (
                <div
                  key={item.id}
                  style={{
                    background: "#F8FAFC",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #F1F5F9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    <div
                      style={{
                        background: "white",
                        padding: "10px",
                        borderRadius: "50%",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <User size={20} color="#64748B" />
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: "700",
                          fontSize: "15px",
                          color: "#1E293B",
                        }}
                      >
                        {item.nama_lengkap}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#64748B",
                          display: "flex",
                          gap: 10,
                        }}
                      >
                        <span>
                          User:{" "}
                          <strong style={{ color: "#2563EB" }}>
                            {item.username}
                          </strong>
                        </span>
                        <span>
                          Pass:{" "}
                          <strong style={{ color: "#059669" }}>
                            {item.password}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: "right",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "11px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        background: getRoleColor(item.role),
                        color: "#334155",
                      }}
                    >
                      {item.role}
                    </span>
                    <button
                      onClick={() => handleEditClick(item)}
                      style={{
                        border: "none",
                        background: "#FFF7ED",
                        color: "#D97706",
                        padding: "8px",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      style={{
                        border: "none",
                        background: "#FEF2F2",
                        color: "#EF4444",
                        padding: "8px",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* --- TAB 2: SINGLE --- */}
      {activeTab === "single" && (
        <div style={styles.card}>
          <h3
            style={{ marginTop: 0, color: isEditing ? "#D97706" : "#2563EB" }}
          >
            {isEditing ? "Edit Akun" : "Tambah Akun Manual"}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "flex", gap: "20px" }}>
              <div style={{ ...styles.inputGroup, flex: 1 }}>
                <label style={styles.label}>Username</label>
                <input
                  style={styles.input}
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  required
                />
              </div>
              <div style={{ ...styles.inputGroup, flex: 1 }}>
                <label style={styles.label}>Password</label>
                <input
                  style={styles.input}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Role</label>
              <select
                style={styles.select}
                value={formData.role}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    role: e.target.value,
                    nama_lengkap: "",
                  })
                }
              >
                <option value="siswa">Siswa</option>
                <option value="tutor">Tutor</option>
                <option value="operator">Operator</option>
                <option value="kepala">Kepala Sekolah</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Nama Lengkap</label>
              {formData.role === "siswa" ? (
                <select
                  style={styles.select}
                  value={formData.nama_lengkap}
                  onChange={(e) =>
                    setFormData({ ...formData, nama_lengkap: e.target.value })
                  }
                  required
                >
                  <option value="">-- Pilih Siswa --</option>
                  {siswaList.map((s, i) => (
                    <option key={i} value={s.nama_lengkap}>
                      {s.nama_lengkap} ({s.kelas})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  style={styles.input}
                  placeholder="Nama Lengkap"
                  value={formData.nama_lengkap}
                  onChange={(e) =>
                    setFormData({ ...formData, nama_lengkap: e.target.value })
                  }
                  required
                />
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="submit" style={styles.btnSave}>
                <Save size={18} /> Simpan
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setActiveTab("list");
                  }}
                  style={styles.btnCancel}
                >
                  <RotateCcw size={18} /> Batal
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* --- TAB 3: BULK GENERATE (MODIFIED) --- */}
      {activeTab === "bulk" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: 20,
            alignItems: "start",
          }}
        >
          <div style={styles.card}>
            <h3
              style={{
                marginTop: 0,
                color: "#2563EB",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Key size={20} /> Setup Otomatis
            </h3>

            <div style={styles.inputGroup}>
              <label style={styles.label}>1. Pilih Role Target</label>
              <select
                style={styles.select}
                value={bulkRole}
                onChange={(e) => {
                  setBulkRole(e.target.value);
                  setSelectedCandidates([]);
                }}
              >
                <option value="siswa">Siswa</option>
                <option value="tutor">Tutor</option>
              </select>
            </div>

            {/* INFO BOX FORMAT PASSWORD */}
            <div
              style={{
                background: "#F0FDF4",
                padding: 15,
                borderRadius: 12,
                border: "1px dashed #BBF7D0",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: "bold",
                  color: "#15803D",
                  marginBottom: 5,
                }}
              >
                FORMAT OTOMATIS
              </div>
              <div style={{ fontSize: 13, color: "#166534", lineHeight: 1.5 }}>
                <strong>Username:</strong> NamaDepan + 3 Angka <br /> (Contoh:{" "}
                <code>budi829</code>)
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#166534",
                  marginTop: 5,
                  lineHeight: 1.5,
                }}
              >
                <strong>Password:</strong> NamaDepan + 4 Angka <br /> (Contoh:{" "}
                <code>budi4921</code>)
              </div>
            </div>

            <div
              style={{
                background: "#F0F9FF",
                padding: 15,
                borderRadius: 12,
                border: "1px dashed #BAE6FD",
                textAlign: "center",
                marginBottom: 20,
              }}
            >
              <div
                style={{ fontSize: 11, fontWeight: "bold", color: "#0369A1" }}
              >
                AKUN AKAN DIBUAT
              </div>
              <div
                style={{ fontSize: 32, fontWeight: "bold", color: "#0284C7" }}
              >
                {selectedCandidates.length}
              </div>
            </div>

            <button
              onClick={handleBulkSubmit}
              disabled={isProcessing}
              style={{
                ...styles.btnSave,
                width: "100%",
                justifyContent: "center",
                opacity: isProcessing ? 0.7 : 1,
              }}
            >
              {isProcessing ? "Memproses..." : "Generate Akun Sekarang"}
            </button>
          </div>

          {/* List Kanan */}
          <div style={styles.card}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 15,
              }}
            >
              <h3 style={{ marginTop: 0, fontSize: 16 }}>
                Pilih Kandidat ({candidatesData.length})
              </h3>
              <button
                onClick={handleSelectAll}
                style={{
                  fontSize: 12,
                  background: "#F1F5F9",
                  border: "none",
                  padding: "5px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Pilih Semua
              </button>
            </div>

            <div style={{ position: "relative", marginBottom: 15 }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: 10,
                  top: 10,
                  color: "#94A3B8",
                }}
              />
              <input
                style={{ ...styles.input, paddingLeft: 35 }}
                placeholder="Cari nama..."
                value={bulkSearch}
                onChange={(e) => setBulkSearch(e.target.value)}
              />
            </div>

            <div
              style={{
                maxHeight: 400,
                overflowY: "auto",
                border: "1px solid #F1F5F9",
                borderRadius: 8,
              }}
            >
              {candidatesData.length === 0 ? (
                <div
                  style={{ padding: 30, textAlign: "center", color: "#94A3B8" }}
                >
                  Semua {bulkRole} sudah punya akun / tidak ditemukan.
                </div>
              ) : (
                candidatesData.map((c, i) => {
                  const isSelected = selectedCandidates.includes(
                    c.nama_lengkap
                  );
                  return (
                    <div
                      key={i}
                      onClick={() => toggleCandidate(c.nama_lengkap)}
                      style={{
                        padding: "10px 15px",
                        borderBottom: "1px solid #F8FAFC",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        background: isSelected ? "#EFF6FF" : "white",
                      }}
                    >
                      <div
                        style={{ color: isSelected ? "#2563EB" : "#CBD5E1" }}
                      >
                        {isSelected ? (
                          <CheckSquare size={20} />
                        ) : (
                          <Square size={20} />
                        )}
                      </div>
                      <div style={{ fontWeight: 600, color: "#334155" }}>
                        {c.nama_lengkap}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes slideDown { from {transform: translate(-50%, -100%); opacity:0;} to {transform: translate(-50%, 0); opacity:1;} }`}</style>
    </div>
  );
}
